import { useState, useEffect, useCallback, useMemo } from "react";
import { copy } from "shared/assets";
import { SelectProps } from "../Select";

export const useSelectLogic = ({
  name,
  value,
  options = [],
  setFieldValue,
  onChange,
  disabled,
  fieldLink,
}: SelectProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isFocused, setFocused] = useState(true);

  useEffect(() => {
    if (isOpen) {
      setFocused(true);
    }
  }, [isOpen]);

  useEffect(() => {
    const closeOnReverseShift = (event: KeyboardEvent) => {
      if (event.shiftKey && event.code === "Tab") {
        setFocused(false);
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", closeOnReverseShift);
    } else {
      document.removeEventListener("keydown", closeOnReverseShift);
    }

    return () => document.removeEventListener("keydown", closeOnReverseShift);
  }, [isOpen]);

  const toggleOpen = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleOpen = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleReset = useCallback(() => {
    if (!setFieldValue) return;

    onChange && onChange({ value: null }, value);
    setFieldValue(name, null);
  }, [name, onChange, setFieldValue, value]);

  const selectedValue = useMemo(() => {
    const foundOption = options.find((option) => option.value === value);

    if (!foundOption && value && typeof value === "string") {
      return {
        value: value,
        label: value,
      };
    }

    return foundOption;
  }, [value, options]);

  const selectFieldLink = useMemo(() => {
    if (fieldLink) return fieldLink;

    return selectedValue
      ? { label: copy.form.reset, onClick: handleReset, tabIndex: -1 }
      : undefined;
  }, [fieldLink, handleReset, selectedValue]);

  // Manually reset value after a react-hook-form reset()
  // For controlled components like React-Select which do not expose a ref prop,
  // you will have to reset the input value manually with setValue
  // https://react-hook-form.com/api/useform/reset
  useEffect(() => {
    if (value === undefined) {
      onChange && onChange(undefined, value);
      setFieldValue && setFieldValue(name, null);
    }
  }, [name, value, setFieldValue, onChange]);

  return {
    isOpen,
    isFocused,
    handleOpen,
    handleClose,
    toggleOpen,
    selectedValue,
    selectFieldLink,
  };
};
